import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, delay, select, take, takeEvery } from 'redux-saga/effects';

import {
  AngebotsId,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  LoadVorvertraege,
  ModalCloseReason,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import {
  AdresseVorvertrag,
  BankverbindungVorvertrag,
  DiagnoseVorvertrag,
  EingenschaftHinweismeldung,
  SparteNeugeschaeftSperren,
  SpartenType,
  SparteVorbelegungdaten,
  StandaloneEinschlussDetail,
  Status,
  Vertragsuebersicht,
  VertragsuebersichtRequestModel,
  VertragsuebersichtResponseModel,
  WarenkorbProduktError,
  WarenkorbValidationResult
} from '@ps20/bff-models';
import {
  App,
  areAppsEquivalent,
  convertVermittlernummerToString,
  GegeVorbelegungdaten,
  getAnzeigenamen,
  getAppEnumBySpartenType,
  getSpartenTypeFromVerkaufsangebotKurznamen,
  Hinweismeldung,
  isHausratSparte,
  mapVorbelegungdaten,
  RisikoortInfo,
  StandaloneProdukt,
  UnfallVorbelegungdaten,
  Vertrag,
  VhvVorbelegungdaten,
  Vorbelegungdaten,
  Vorvertrag,
  VorvertragAdresse,
  VwgVorbelegungdaten
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, getVersicherteGegeText } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import content from '../../data/content-default.json';
import { NeugeschaeftSperren } from '../model/neugeschaeftSperren';
import {
  isStandaloneVertrag,
  isVorvertrag,
  selectInvalidWarenkorbProdukte,
  selectOnlyWeitereVertraege,
} from '../selectors/vorvertragsUebersicht.selector';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { mapVertragItemToVorvertrag } from '../reducers/weitereVertraege.reducer';
import {
  buildInvalidWarenkorbErrorText,
  getInvalideWarenkorbAngebote,
} from './helper-functions/invalidWarenkorbErrorHelper';
import { mapWarenkorbProdukte } from './warenkorbMapper';
import {
  getBasicDaten,
  getBasicDatenForWeiterenVorvertrag,
} from './personensucheAndUpdateVertraegeSaga';

export const VP_PREFIX = 'VP: ';
export const VORVERTRAGSUEBERSICHT_URL = '/privatschutz/api/vertragsuebersicht';

interface VertragsuebersichtWithDiagnoses extends Vertragsuebersicht {
  previousTkvDiagnoses?: DiagnoseVorvertrag[];
}

export enum VORVERTRAGSUEBERSICHT_EVENTS {
  REQUEST_VORVERTRAGSUEBERSICHT_SUCCESS = 'REQUEST_VORVERTRAGSUEBERSICHT_SUCCESS',
  REQUEST_VORVERTRAGSUEBERSICHT_FAILED = 'REQUEST_VORVERTRAGSUEBERSICHT_FAILED',
}

export function* vorvertragsUebersichtSaga(): SagaIterator {
  yield takeEvery(
    GlobalActions.LOAD_VORVERTRAEGE,
    safeSaga(requestVorvertraege, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_SUCCESS,
    safeSaga(handleVorvertraegeResponse, App.SHOP_DASHBOARD),
  );
  yield takeEvery(
    VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_FAILED,
    safeSaga(errorCallback, App.SHOP_DASHBOARD),
  );
}

export function* requestVorvertraege(action: GlobalAction<LoadVorvertraege>): SagaIterator {
  const state: IAppState = yield select();

  const actionPayload = action.payload;
  const kundennummer = actionPayload.kundennummer;
  const perId = actionPayload.perId;
  const vermittlernummerString = convertVermittlernummerToString(actionPayload.vermittlernummer);

  const requestModel: VertragsuebersichtRequestModel = {
    kundennummer: kundennummer,
    warenkorbProdukteToValidate: mapWarenkorbProdukte(state),
    gesellschForm: state.versicherungsnehmer.businessForm?.wert,
  };
  if (vermittlernummerString !== '') {
    requestModel.vertretungsnummer = vermittlernummerString;
  }
  if (perId !== '') {
    requestModel.pnr = perId;
  }
  requestModel.loadBankverbindungen = actionPayload.loadBankverbindungen;

  if (state.transientState.updatefremdvertraegeIsInProgress) {
    requestModel.fremdStandaloneProdukte = state.transientState.newFremdVertraegeState
      .filter(vertrag => isStandaloneVertrag(vertrag))
      .map(vertrag => getBasicDaten(vertrag));
    requestModel.fremdStandardProdukte = state.transientState.newFremdVertraegeState
      .filter(vertrag => !isStandaloneVertrag(vertrag))
      .map(vertrag => getBasicDaten(vertrag));
  } else {
    requestModel.fremdStandardProdukte = state.fremdVertraege
      .filter(vertrag => isVorvertrag(vertrag))
      .map(vertrag => getBasicDaten(vertrag));
    requestModel.fremdStandaloneProdukte = state.fremdVertraege
      .filter(vertrag => isStandaloneVertrag(vertrag))
      .map(vertrag => getBasicDaten(vertrag));
  }

  if (state.transientState.updateWeitereVertraegeIsInProgress) {
    const mappedItems = state.transientState.weitereVertraegeCached
      .map(item => mapVertragItemToVorvertrag(item))
      .map(value => getBasicDatenForWeiterenVorvertrag(value));

    requestModel.weitereStandardProdukte = mappedItems;
  } else {
    requestModel.weitereStandardProdukte = selectOnlyWeitereVertraege(state).map(value =>
      getBasicDatenForWeiterenVorvertrag(value),
    );
  }

  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.SHOP_CHECKOUT,
      url: VORVERTRAGSUEBERSICHT_URL,
      body: requestModel,
      successEvent: VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_SUCCESS,
      errorEvent: VORVERTRAGSUEBERSICHT_EVENTS.REQUEST_VORVERTRAGSUEBERSICHT_FAILED,
    }),
  );
}

export function* handleVorvertraegeResponse(
  successAction: DoRequestResponse<VertragsuebersichtResponseModel>,
): SagaIterator {
  const state: IAppState = yield select();
  if (
    state.transientState.updatefremdvertraegeIsInProgress ||
    state.transientState.updateWeitereVertraegeIsInProgress
  ) {
    return;
  }
  const globalPut = GlobalPut.getInstance();
  const globalActions = new GlobalActions();
  const origResponse: VertragsuebersichtResponseModel = successAction.payload;
  const vorvertraege: Array<Vorvertrag> = extractStandardVertraegeFromResponse(origResponse);
  const standaloneProdukte: Array<StandaloneProdukt> =
    extractStandaloneVertraegeFromResponse(origResponse);
  const bankverbindungen: Array<BankverbindungVorvertrag> = origResponse.bankverbindungen;

  const isVorgangValid =
    origResponse.invalidWarenkorbProdukte == null ||
    origResponse.invalidWarenkorbProdukte.length === 0;

  if (isVorgangValid) {
    yield call(
      globalPut.put,
      globalActions.updateVorvertraege(
        vorvertraege,
        standaloneProdukte,
        bankverbindungen,
        origResponse.maxLimitUmstellungszwangErreicht,
      ),
    );
  } else {
    const vertraegeLog =
      'saved standard Vorvertraege: ' +
      JSON.stringify(state.vorvertraege) +
      ', current standard Vorvertraege: ' +
      JSON.stringify(vorvertraege) +
      ', saved standalone Vorvertraege: ' +
      JSON.stringify(state.standaloneProdukte) +
      ', current standalone Vorvertraege: ' +
      JSON.stringify(standaloneProdukte);
    PS20Logger.info(
      'VORVERTRAEGE_STATUS_CHANGED_ERROR.' +
        'Der Status der Vorverträge hat sich nach dem letzten Speichern wesentlich geändert.',
      vertraegeLog,
      App.SHOP_DASHBOARD,
    );

    const invalidWarenkorbProdukte: WarenkorbValidationResult[] = selectInvalidWarenkorbProdukte(
      origResponse.invalidWarenkorbProdukte,
      state,
    );
    const allInvalidProdukteHadStatusBeibehalten = invalidWarenkorbProdukte.find(produkt =>
      [
        WarenkorbProduktError.BEIBEHALTENEN_VORVERTRAG_NICHT_GUELTIG,
        WarenkorbProduktError.BEIBEHALTENEN_VORVERTRAG_WURDE_GEAENDERT,
        WarenkorbProduktError.TKV_TIERART_NICHT_MEHR_GUELTIG,
      ].includes(produkt.validationError),
    );

    if (invalidWarenkorbProdukte?.length > 0 && allInvalidProdukteHadStatusBeibehalten) {
      const alleInvalideAngebote: Array<WarenkorbAngebot> = getInvalideWarenkorbAngebote(
        state.warenkorb,
        invalidWarenkorbProdukte,
      );
      const hinweisText: string = buildInvalidWarenkorbErrorText(alleInvalideAngebote);
      yield call(
        globalPut.put,
        globalActions.vorvertraegeLoadFinished(),
      );
      yield delay(0);
      yield call(
        globalPut.put,
        globalActions.requestModal(
          (ModalType as Record<string, ModalType>)[allInvalidProdukteHadStatusBeibehalten.validationError],
          { body: hinweisText },
        ),
      );

      const alleInvalideAngebotsIds: Array<AngebotsId> = alleInvalideAngebote.map(angebot => ({
        angebotsId: angebot.angebotsId,
        appId: angebot.appId,
      }));

      const closeModalAction = yield take(GlobalActions.CLOSE_MODAL);
      if (closeModalAction.payload === ModalCloseReason.PRIMARY) {
        yield call(
          globalPut.put,
          globalActions.updateVorvertraege(
            vorvertraege,
            standaloneProdukte,
            bankverbindungen,
            origResponse.maxLimitUmstellungszwangErreicht,
          ),
        );
        yield call(
          globalPut.put,
          globalActions.produktDeleted(alleInvalideAngebotsIds),
        );
      }
    } else {
      yield call(globalPut.put, globalActions.updateVorvertraegeFailed());
      yield delay(0);
      yield call(
        [globalPut, globalPut.put],
        globalActions.requestModal(ModalType.VORVERTRAEGE_STATUS_CHANGED),
      );
    }
  }
}

export function extractStandardVertraegeFromResponse(
  response: VertragsuebersichtResponseModel,
): Array<Vorvertrag> {
  if (!response.standardProdukte) {
    return [];
  }
  return response.standardProdukte.map(vertragsuebersicht => {
    const basicData = mapVertragsuebersicht(vertragsuebersicht);

    const spartenTypeFromKurznamen = getSpartenTypeFromVerkaufsangebotKurznamen(
      vertragsuebersicht.verkaufsangebotKurznamen,
    );

    const spartenTypes = vertragsuebersicht.spartenTypes.map(spartenType => {
      if (
        spartenTypeFromKurznamen === SpartenType.TIERKRANKEN &&
        spartenType === SpartenType.TIERKRANKEN_V2
      ) {
        return SpartenType.TIERKRANKEN;
      }
      if (
        spartenTypeFromKurznamen === SpartenType.TIERKRANKEN_V2 &&
        spartenType === SpartenType.TIERKRANKEN
      ) {
        return SpartenType.TIERKRANKEN_V2;
      }
      if (
        spartenTypeFromKurznamen === SpartenType.WOHNGEBAEUDE &&
        spartenType === SpartenType.WOHNGEBAEUDE_V2
      ) {
        return SpartenType.WOHNGEBAEUDE;
      }
      if (
        spartenTypeFromKurznamen === SpartenType.WOHNGEBAEUDE_V2 &&
        spartenType === SpartenType.WOHNGEBAEUDE
      ) {
        return SpartenType.WOHNGEBAEUDE_V2;
      }
      return spartenType;
    });

    const appIds: App[] = spartenTypes.map(spartenType => getAppEnumBySpartenType(spartenType));
    return {
      ...basicData,
      produktIds: appIds,
      produktlinieAnzeigename: vertragsuebersicht.produktlinieAnzeigename,
      neugeschaeftSperren: mapNeugeschaeftSperren(vertragsuebersicht.neugeschaeftSperren),
      icon: getDashboardIcons(spartenTypes),
      risikoortInfo: mapRisikoortAnzeige(
        appIds,
        vertragsuebersicht.ersetzen,
        basicData.vorbelegungdaten,
      ),
      infoText: vertragsuebersicht.infoText,
      vorbelegungAktiv: vertragsuebersicht.vorbelegungAktiv,
      umstellungszwang: vertragsuebersicht.umstellungszwang,
      tarifGenerationPS10: vertragsuebersicht.tarifGenerationPS10,
      tarifGenerationPS20: vertragsuebersicht.tarifGenerationPS20,
      beibehaltung: vertragsuebersicht.beibehaltung,
      ruhend: vertragsuebersicht.ruhend,
      firmenvertrag: vertragsuebersicht.firmenvertrag,
      aenderungdatum: vertragsuebersicht.wirksamkeitsbeginn,
      vorvertragSofortschutzAblaufDatum: vertragsuebersicht.vorvertragSofortschutzAblaufDatum,
    };
  });
}

export function extractStandaloneVertraegeFromResponse(
  response: VertragsuebersichtResponseModel,
): Array<StandaloneProdukt> {
  if (!response.standaloneProdukte) {
    return [];
  }

  return response.standaloneProdukte.map(standaloneProdukt => {
    const basicData = mapVertragsuebersicht(standaloneProdukt);
    const einschliessenData = standaloneProdukt.einschliessen.map(detail => ({
      appId: getAppEnumBySpartenType(detail.spartenType),
      pflichtZusatzoptionen: detail.pflichtZusatzoptionen,
    }));
    const appIds: App[] = einschliessenData.map(einschl => einschl.appId);

    return {
      ...basicData,
      einschliessenDetails: einschliessenData,
      einschliessenPflichtErrorText: standaloneProdukt.einschlPflichtErrorText,
      einschliessenderVertrag: standaloneProdukt.inProduktVorhanden
        ? standaloneProdukt.inProduktVorhanden
        : [],
      risikoortInfo: mapRisikoortAnzeige(
        appIds,
        standaloneProdukt.ersetzen,
        basicData.vorbelegungdaten,
      ),
      infoText: standaloneProdukt.infoText
        ? standaloneProdukt.infoText
        : buildStandaloneInfoText(standaloneProdukt.einschliessen),
      icon: 'product-shield',
    };
  });
}

function mapVertragsuebersicht(vorvertrag: Vertragsuebersicht): Vertrag {
  const vorvertragWithDiagnoses = vorvertrag as VertragsuebersichtWithDiagnoses;
  const vorbelegungdaten = mapBffVorbelegungdaten(
    vorvertragWithDiagnoses.vorbelegungdaten,
    vorvertragWithDiagnoses.previousTkvDiagnoses,
  );

  return {
    vertragsnummer: vorvertrag.vertragsnummer,
    linkToSAbs: vorvertrag.linkToSAbs,
    zusatzinformation: vorvertrag.zusatzinformation,
    zahlungsperiode: vorvertrag.zahlungsperiode,
    zahlungsart: vorvertrag.zahlungsart,
    isAssekuranztarif: vorvertrag.assekuranztarif,
    berechnetePraemieBrutto: vorvertrag.berechnetePraemieBrutto,
    jahresbestandpraemieNetto: vorvertrag.jahresbestandpraemieNetto,
    jahresbestandpraemieBrutto: vorvertrag.jahresbestandpraemieBrutto,
    spLaufnummer: vorvertrag.spLaufnummer,
    anzeigename: vorvertrag.anzeigename,
    ersetzen: vorvertrag.ersetzen,
    eigenschaftHinweise: mapHinweisMeldungen(vorvertrag.hinweismeldungen),
    infoText: null,
    icon: null,
    vorvertragSofortschutz: vorvertrag.vorvertragSofortschutz,
    isStorniert: vorvertrag.storniert,
    stornoDetail: vorvertrag.stornoDetail,
    verkaufsangebotKurznamen: vorvertrag.verkaufsangebotKurznamen,
    vorbelegungdaten,
    risikoortInfo: null,
    vertragskennzeichen: vorvertrag.vertragskennzeichen,
    ablaufdatum: vorvertrag.ablaufdatum,
    hasMultipleVn: vorvertrag.hasMultipleVn,
  };
}

export function mapRisikoortAnzeige(
  appIds: App[],
  ersetzen: boolean,
  vorbelegungdaten: Vorbelegungdaten[],
): RisikoortInfo | null {
  const umstellbar = ersetzen;
  const isVwg = appIds.some(appId => areAppsEquivalent(App.VWG, appId));
  const isVHv = appIds.some(appId => areAppsEquivalent(App.VHV, appId));
  const isUnfall = appIds.includes(App.UNF);
  const isGege = appIds.includes(App.GEGE);
  const anzeigeForNichtUmstellbarVwgVhv: RisikoortInfo = {
    valid: true,
    anzeige: '',
  };

  if (isVwg) {
    return umstellbar ? getVwgRisikoortAnzeige(vorbelegungdaten) : anzeigeForNichtUmstellbarVwgVhv;
  } else if (isVHv) {
    return umstellbar ? getVhvRisikoortAnzeige(vorbelegungdaten) : anzeigeForNichtUmstellbarVwgVhv;
  } else if (isUnfall) {
    return umstellbar ? getUnfallVPInfosAnzeige(vorbelegungdaten) : anzeigeForNichtUmstellbarVwgVhv;
  } else if (isGege) {
    return umstellbar
      ? {
          valid: true,
          anzeige: getVersichertesRisikoGege(vorbelegungdaten),
        }
      : anzeigeForNichtUmstellbarVwgVhv;
  } else {
    return null;
  }
}

export const getVersichertesRisikoGege = (vorbelegungdaten: Vorbelegungdaten[]) => {
  const gegeVorbelegungsDaten: GegeVorbelegungdaten = vorbelegungdaten?.find(
    vorbe => vorbe.forApp === App.GEGE,
  ) as GegeVorbelegungdaten;
  if (gegeVorbelegungsDaten) {
    const gegenstaende = gegeVorbelegungsDaten.versicherteGegenstaende;
    const mappedGegenstaende = gegenstaende.map(gegenstand => gegenstand.category);
    return getVersicherteGegeText(mappedGegenstaende);
  }
};

function getVwgRisikoortAnzeige(vorbelegungdaten: Vorbelegungdaten[]): RisikoortInfo {
  let risikoort;
  const vwgDaten: Vorbelegungdaten = vorbelegungdaten?.find(vorbe => areAppsEquivalent(vorbe.forApp, App.VWG));
  if (vwgDaten) {
    const vwgVorbelegungDaten = vwgDaten as VwgVorbelegungdaten;
    risikoort = vwgVorbelegungDaten.risikoort;
  }
  return getRisikoortAnzeige(risikoort, 'Gebäude: ');
}

export function getPrintedVpDaten(
  prefix: string,
  person: { vorname: string; nachname: string; geburtsdatum: string },
) {
  return prefix + person.vorname + ' ' + person.nachname + ', geb. ' + person.geburtsdatum;
}

export function getUnfallVPInfosAnzeige(vorbelegungdaten: Vorbelegungdaten[]): RisikoortInfo {
  const unfDaten: Vorbelegungdaten = vorbelegungdaten?.find(vorbe => vorbe.forApp === App.UNF);

  if (!unfDaten) {
    return undefined;
  }

  const unfVorbelegungDaten = unfDaten as UnfallVorbelegungdaten;
  const adresse: AdresseVorvertrag = unfVorbelegungDaten.vpAdresse;
  const vpDaten = {
    vorname: unfVorbelegungDaten.vorname,
    nachname: unfVorbelegungDaten.nachname,
    geburtsdatum: dayjs(unfVorbelegungDaten.geburtsdatum).format(DATEFORMAT_LOCAL_STATE),
  };

  if (!adresse || adresse.status === Status.NOK) {
    return getRisikoortAnzeige(
      adresse,
      'dummy',
      getPrintedVpDaten('VP-Daten ungültig: ', vpDaten) + ', ',
      'VP-Daten fehlen!',
    );
  }

  if (!unfVorbelegungDaten.geburtsdatum) {
    return {
      valid: false,
      anzeige: getPrintedVpDaten('VP-Daten ungültig: ', vpDaten),
    };
  }

  return {
    valid: true,
    anzeige: getPrintedVpDaten(VP_PREFIX, vpDaten),
  };
}

function getVhvRisikoortAnzeige(vorbelegungdaten: Vorbelegungdaten[]): RisikoortInfo {
  let risikoort;
  const vhvDaten: Vorbelegungdaten = vorbelegungdaten?.find(vorbe => areAppsEquivalent(vorbe.forApp, App.VHV));
  if (vhvDaten) {
    const vhvVorbelegungDaten = vhvDaten as VhvVorbelegungdaten;
    risikoort = vhvVorbelegungDaten.risikoort;
  }
  return getRisikoortAnzeige(risikoort, 'Wohnung: ');
}

function getRisikoortAnzeige(
  risikoort: VorvertragAdresse,
  prefix: string,
  anzeigeUngueltig?: string,
  anzeigeFehlt?: string,
): RisikoortInfo {
  if (!anzeigeUngueltig) {
    anzeigeUngueltig = 'Adresse ungültig: ';
  }
  if (!anzeigeFehlt) {
    anzeigeFehlt = 'Adresse fehlt!';
  }
  if (risikoort) {
    if (risikoort.status === Status.OK) {
      return { valid: true, anzeige: prefix + getAdresseInString(risikoort) };
    }
    return {
      valid: false,
      anzeige: anzeigeUngueltig + getAdresseInString(risikoort),
    };
  }
  return { valid: false, anzeige: anzeigeFehlt };
}

function getAdresseInString(risikoort: VorvertragAdresse): string {
  return (
    `${getStringNullSafe(risikoort.strasse)} ${getStringNullSafe(risikoort.hausnummer)},` +
    ` ${getStringNullSafe(risikoort.plz)} ${getStringNullSafe(risikoort.ort)}`
  );
}

function getStringNullSafe(anzeige: string): string {
  return anzeige || '';
}

export function mapHinweisMeldungen(
  eingenschaftHinweismeldung: EingenschaftHinweismeldung[],
): Hinweismeldung[] {
  if (eingenschaftHinweismeldung) {
    return eingenschaftHinweismeldung.map(hinweismeldung => {
      const meldung: Hinweismeldung = {
        eigenschaftHinweis: hinweismeldung.eigenschaftHinweise,
        meldung: hinweismeldung.meldung,
      };
      return meldung;
    });
  }
  return [];
}

export function* errorCallback(): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  yield call(globalPut.put, new GlobalActions().errorPageWithCustomContentRequested('vorvertrag'));
}

export function getDashboardIcons(spartenTypes: SpartenType[]): string {
  if (
    spartenTypes.filter(
      spartenType => spartenType === SpartenType.PRIVATHAFTPFLICHT || isHausratSparte(spartenType),
    ).length === 2
  ) {
    return 'product-broken-window';
  } else {
    return {
      [SpartenType.WOHNGEBAEUDE]: 'house-window',
      [SpartenType.WOHNGEBAEUDE_V2]: 'house-window',
      [SpartenType.HAUSRAT]: 'couch',
      [SpartenType.HAUSRAT_V2]: 'couch',
      [SpartenType.PRIVATHAFTPFLICHT]: 'private-haftpflicht',
      [SpartenType.RECHTSSCHUTZ]: 'product-balance-scale',
      [SpartenType.RECHTSSCHUTZ_VERMIETUNG]: 'vermieter-rechtsschutz',
      [SpartenType.TIERHALTERHAFTPFLICHT_HUND]: 'tierhalterhaftpflicht',
      [SpartenType.TIERHALTERHAFTPFLICHT_PFERD]: 'tierhalterhaftpflicht',
      [SpartenType.TIERKRANKEN]: 'tierkranken',
      [SpartenType.TIERKRANKEN_V2]: 'tierkranken',
      [SpartenType.UNFALL]: 'product-unfall',
      [SpartenType.JAGDHAFTPFLICHT]: 'hunting-deer',
      [SpartenType.GEGENSTANDSSCHUTZ]: 'gege',
    }[spartenTypes[0]];
  }
}

export function buildStandaloneInfoText(einschlussDetail: StandaloneEinschlussDetail[]): string {
  const infotext = content['uebersicht']['standaloneProdukt']['infotext'];
  const produktList = einschlussDetail
    .map(produkt => getAnzeigenamen(getAppEnumBySpartenType(produkt.spartenType)))
    .join(', ');
  return infotext + produktList;
}

export function mapBffVorbelegungdaten(
  vorbelegungdaten: SparteVorbelegungdaten[],
  previousTkvDiagnoses?: DiagnoseVorvertrag[],
): Vorbelegungdaten[] {
  return vorbelegungdaten?.map(vorbe => {
    let vorbelegungdatenToMap = vorbe.vorbelegungdaten;

    const spartenApp = getAppEnumBySpartenType(vorbe.spartenType);
    if (areAppsEquivalent(spartenApp, App.TKV) && previousTkvDiagnoses) {
      vorbelegungdatenToMap = {
        ...vorbe.vorbelegungdaten,
        diagnose: previousTkvDiagnoses,
      };
    }

    return mapVorbelegungdaten(vorbe.spartenType, vorbelegungdatenToMap);

  });
}

export function mapNeugeschaeftSperren(
  neugeschaeftSperren: SparteNeugeschaeftSperren[],
): NeugeschaeftSperren[] {
  return neugeschaeftSperren?.map(neugeschaeft => ({
    appId: getAppEnumBySpartenType(neugeschaeft.spartenType),
    neugeschaeftSperren: neugeschaeft.neugeschaeftSperren,
  }));
}
